import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import Dialog from "src/components/Dialog";
import Button from "@material-ui/core/Button";

import LoadableButton from "src/components/LoadableButton";
import { getCallableErrorMessage } from "src/utils/callableErrors";
import { getEBayIntegrations } from "src/store/plaidIntegration/selector";
import { allowedEBayIntegrationsSelector } from "src/store/system/selector";
import useStripeLink from "src/utils/useStripeLink";
import { getEBayOAuthUrl } from "src/apiService/modules/ebay";
import AddNewSalePlatformIntegrationDialog, {
  useHasExistingSalePlatforms,
} from "src/components/AddNewSalePlatformIntegrationDialog";

export function WantToAddAdditionalEBayStoresContent({
  onClose,
}: {
  onClose: () => void;
}) {
  const link = useStripeLink();

  return (
    <>
      <DialogTitle>Want to add additional eBay stores?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Ultimate users can connect additional eBay stores for an extra
          $5/month/store. In the portal, set your plan to ”Ultimate” and
          increase the quantity of the "Ultimate" plan to the number of stores
          you'd like to integrate. Use the "Upgrade" button below to access the
          customer billing portal.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>No thanks</Button>
        <LoadableButton
          color="primary"
          autoFocus
          variant="contained"
          loading={!link}
          href={link}
          {...({ target: "_blank" } as any)}
        >
          Upgrade
        </LoadableButton>
      </DialogActions>
    </>
  );
}

export function useCanAddMoreEBayIntegrations() {
  const integrations = useSelector(getEBayIntegrations);
  const allowedEBayIntegrations = useSelector(allowedEBayIntegrationsSelector);
  const canAddIntegrations = integrations.length < allowedEBayIntegrations;
  return canAddIntegrations;
}

export interface AddNewIntegrationProps {
  open: boolean;
  onClose: () => void;
}

function AddNewEBayIntegration({ open, onClose }: AddNewIntegrationProps) {
  const [loading, setLoading] = useState(false);
  const hasExistingPlatforms = useHasExistingSalePlatforms();

  const handleConnect = async (bankAccountId?: string) => {
    setLoading(true);
    try {
      const {
        data: { url },
      } = await getEBayOAuthUrl({
        bankAccountId: bankAccountId || "add-new",
      });
      window.location = url;
      onClose();
    } catch (e) {
      toast.error(getCallableErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  // Store handleConnect in a ref so the effect doesn't re-run when the handler changes
  const handleConnectRef = useRef(handleConnect);
  handleConnectRef.current = handleConnect;

  // Auto-proceed to OAuth when no existing platforms to choose from
  const hasAutoProceeded = useRef(false);
  useEffect(() => {
    if (open && !hasExistingPlatforms && !hasAutoProceeded.current) {
      hasAutoProceeded.current = true;
      handleConnectRef.current(undefined);
    }
  }, [open, hasExistingPlatforms]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setLoading(false);
      hasAutoProceeded.current = false;
    }
  }, [open]);

  // Don't render the dialog if there are no existing platforms to choose from
  if (!hasExistingPlatforms) {
    return null;
  }

  return (
    <AddNewSalePlatformIntegrationDialog
      open={open}
      loading={loading}
      onClose={onClose}
      platformName="eBay"
      onConnect={handleConnect}
    />
  );
}

function AddNewIntegration({ open, onClose }: AddNewIntegrationProps) {
  const canAddIntegrations = useCanAddMoreEBayIntegrations();

  if (canAddIntegrations)
    return <AddNewEBayIntegration open={open} onClose={onClose} />;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <WantToAddAdditionalEBayStoresContent onClose={onClose} />
    </Dialog>
  );
}

export default AddNewIntegration;
